import { formatLocalZipFileLocation } from './localFilePathHint';

/** ローカル Zip 正本ツールの識別子（localStorage キーに使う） */
export type LocalZipToolId = 'sample-manager' | 'tpp' | 'labo-suke';

export type LocalZipRecentEntry = {
  name: string;
  /** OS 絶対パス等（ブラウザが提供するときのみ） */
  path: string | null;
  updatedAt: string;
};

const STORAGE_PREFIX = 'kensuke.localZip.recent.';
const MAX_ENTRIES = 8;

function storageKey(toolId: LocalZipToolId): string {
  return `${STORAGE_PREFIX}${toolId}`;
}

function readRaw(toolId: LocalZipToolId): LocalZipRecentEntry[] {
  try {
    const raw = localStorage.getItem(storageKey(toolId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((row): LocalZipRecentEntry | null => {
        if (!row || typeof row !== 'object') return null;
        const o = row as Record<string, unknown>;
        const name = typeof o.name === 'string' ? o.name.trim() : '';
        if (!name) return null;
        const path =
          typeof o.path === 'string' && o.path.trim() && o.path.trim() !== name
            ? o.path.trim()
            : null;
        const updatedAt =
          typeof o.updatedAt === 'string' && o.updatedAt ? o.updatedAt : new Date(0).toISOString();
        return { name, path, updatedAt };
      })
      .filter((e): e is LocalZipRecentEntry => e != null);
  } catch {
    return [];
  }
}

function writeRaw(toolId: LocalZipToolId, entries: LocalZipRecentEntry[]): void {
  try {
    localStorage.setItem(storageKey(toolId), JSON.stringify(entries.slice(0, MAX_ENTRIES)));
  } catch {
    /* private mode 等 */
  }
}

function sameFile(a: LocalZipRecentEntry, b: { name: string; path?: string | null }): boolean {
  const bPath = b.path?.trim() || null;
  if (a.path && bPath) return a.path === bPath;
  return a.name === b.name && (a.path ?? null) === bPath;
}

/** 直近に開いた／保存したローカル Zip を記録（先頭が最新） */
export function rememberLocalZipRecent(
  toolId: LocalZipToolId,
  entry: { name: string; path?: string | null }
): void {
  const name = entry.name.trim();
  if (!name) return;
  const path =
    typeof entry.path === 'string' && entry.path.trim() && entry.path.trim() !== name
      ? entry.path.trim()
      : null;
  const next: LocalZipRecentEntry = {
    name,
    path,
    updatedAt: new Date().toISOString(),
  };
  const prev = readRaw(toolId).filter((e) => !sameFile(e, next));
  // 同名で path が後から取れた場合、古い name-only を落とす
  const deduped = prev.filter((e) => !(e.name === next.name && !e.path && next.path));
  writeRaw(toolId, [next, ...deduped]);
}

export function listLocalZipRecent(
  toolId: LocalZipToolId,
  limit: number = MAX_ENTRIES
): LocalZipRecentEntry[] {
  return readRaw(toolId).slice(0, Math.max(0, limit));
}

export function getLatestLocalZipRecent(toolId: LocalZipToolId): LocalZipRecentEntry | null {
  return listLocalZipRecent(toolId, 1)[0] ?? null;
}

export function clearLocalZipRecent(toolId: LocalZipToolId): void {
  try {
    localStorage.removeItem(storageKey(toolId));
  } catch {
    /* ignore */
  }
}

/** 旧 `*.lastZipFileName` キーからの一度きりの取り込み */
export function migrateLegacyLastZipFileName(
  toolId: LocalZipToolId,
  legacyKey: string
): LocalZipRecentEntry | null {
  const existing = getLatestLocalZipRecent(toolId);
  if (existing) return existing;
  try {
    const name = localStorage.getItem(legacyKey)?.trim();
    if (!name) return null;
    rememberLocalZipRecent(toolId, { name, path: null });
    return getLatestLocalZipRecent(toolId);
  } catch {
    return null;
  }
}

export function localZipRecentDisplayPath(entry: LocalZipRecentEntry): string {
  return formatLocalZipFileLocation(entry);
}
